import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { Ambulance, ArrowRight, ArrowLeft } from "lucide-react";

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const idleNotice = new URLSearchParams(location.search).get("reason") === "idle";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const [step, setStep] = useState("credentials"); // "credentials" | "mfa"
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  const landingPath = (u) =>
    u.mfa_required
      ? u.role === "driver"
        ? "/driver/account?mfa=required"
        : "/app/account?mfa=required"
      : u.role === "driver"
      ? "/driver"
      : "/app";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result && result.mfaRequired) {
        setMfaToken(result.mfaToken);
        setStep("mfa");
      } else {
        nav(landingPath(result));
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await verifyMfa(mfaToken, code);
      nav(u.role === "driver" ? "/driver" : "/app");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const backToSignIn = () => {
    setStep("credentials");
    setErr("");
    setCode("");
    setUseRecovery(false);
    setMfaToken(null);
  };

  const inputCls = "mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300";
  const labelCls = "text-xs font-semibold uppercase tracking-[0.1em] text-slate-500";

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-white">
      {/* Left */}
      <div className="lg:col-span-2 flex flex-col p-10 lg:p-14 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#0A192F] flex items-center justify-center">
            <Ambulance className="text-white" size={20} />
          </div>
          <div className="font-display font-semibold text-lg">{BRAND}</div>
        </div>

        <div className="max-w-md w-full mx-auto lg:mx-0 my-10">
          {step === "credentials" ? (
            <>
              <div className="text-xs font-semibold tracking-[0.14em] text-[#005E82] uppercase mb-3">
                NEMT Operations Suite
              </div>
              <h1 className="font-display text-4xl font-bold text-[#0A192F] leading-tight">
                Move patients.<br />
                Manage everything else.
              </h1>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                Onboard your fleet, dispatch rides, and let client companies submit trips via portal, CSV, or API, all from one clean workspace.
              </p>

              {idleNotice && (
                <div
                  className="mt-6 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
                  data-testid="login-idle-notice"
                >
                  You were signed out after a period of inactivity.
                </div>
              )}

              <form onSubmit={submit} className="mt-10 space-y-4" data-testid="login-form">
                <div>
                  <label className={labelCls}>Email</label>
                  <input
                    data-testid="login-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputCls}
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>Password</label>
                  <input
                    data-testid="login-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputCls}
                    required
                  />
                </div>
                {err && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">
                    {err}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="login-submit"
                >
                  {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
                </button>
              </form>

              <div className="mt-6 text-sm text-slate-500">
                New transportation agency?{" "}
                <Link className="text-[#005E82] font-medium hover:underline" to="/register" data-testid="link-register">
                  Create your account
                </Link>
              </div>
              <div className="mt-2 text-sm text-slate-500">
                <Link className="text-[#005E82] font-medium hover:underline" to="/forgot" data-testid="link-forgot">
                  Forgot your password?
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="text-xs font-semibold tracking-[0.14em] text-[#005E82] uppercase mb-3">
                NEMT Operations Suite
              </div>
              <h1 className="font-display text-3xl font-bold text-[#0A192F] leading-tight">
                Enter your verification code
              </h1>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                {useRecovery
                  ? "Enter one of the recovery codes you saved when you set up MFA (for example abcde-fghij)."
                  : `Open your authenticator app and enter the 6-digit code for ${BRAND}.`}
              </p>

              <form onSubmit={submitMfa} className="mt-8 space-y-4" data-testid="login-mfa-form">
                <div>
                  <label className={labelCls}>{useRecovery ? "Recovery code" : "Verification code"}</label>
                  <input
                    data-testid="login-mfa-code"
                    type="text"
                    autoFocus
                    autoComplete="one-time-code"
                    inputMode={useRecovery ? undefined : "numeric"}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className={inputCls}
                    required
                  />
                </div>
                {err && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">
                    {err}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="login-mfa-submit"
                >
                  {loading ? "Verifying…" : "Verify"} <ArrowRight size={16} />
                </button>
              </form>

              <div className="mt-6 text-sm text-slate-500">
                <button
                  type="button"
                  onClick={() => {
                    setUseRecovery((v) => !v);
                    setCode("");
                    setErr("");
                  }}
                  className="text-[#005E82] font-medium hover:underline"
                  data-testid="login-mfa-use-recovery"
                >
                  {useRecovery ? "Use your authenticator app instead" : "Use a recovery code instead"}
                </button>
              </div>
              <div className="mt-2 text-sm text-slate-500">
                <button
                  type="button"
                  onClick={backToSignIn}
                  className="text-slate-500 font-medium hover:underline inline-flex items-center gap-1"
                  data-testid="login-mfa-back"
                >
                  <ArrowLeft size={14} /> Back to sign in
                </button>
              </div>
            </>
          )}
        </div>

        <div className="text-xs text-slate-400">© 2026 {BRAND} · HIPAA-conscious operations</div>
      </div>

      {/* Right — hero image */}
      <div className="hidden lg:block lg:col-span-3 relative">
        <img
          src="https://images.pexels.com/photos/5025517/pexels-photo-5025517.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400"
          alt="Medical transport"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A192F]/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="grid grid-cols-3 gap-4 max-w-2xl">
            <Stat label="Trips dispatched" value="12,480+" />
            <Stat label="Client partners" value="86" />
            <Stat label="On-time rate" value="97.4%" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l-2 border-white/60 pl-3">
      <div className="text-xs uppercase tracking-[0.15em] text-white/70">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
